<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('country.subtitle') }} {{ $t('country.adviceText') }}</span>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          {{ $t('country.country') }}
          <span class="ov-count">{{ paysList.length }}</span>
        </div>
        <div class="ov-filters">
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('country.searchCountry')" />
          </div>
          <button type="button" class="ov-btn ov-btn--primary ov-btn--sm" @click="openCreate">
            <v-icon small>mdi-plus</v-icon>
            {{ $t('country.addCountry') }}
          </button>
        </div>
      </div>

      <v-data-table
        :headers="tableHeaders"
        :items="paysList"
        :search="search"
        :loading="loadingList"
        loader-height="2"
        loading-text="Chargement des pays..."
        no-data-text="Aucun pays enregistré"
        no-results-text="Aucun pays ne correspond à la recherche"
        class="ov-table"
      >
        <template v-slot:[`item.designation`]="{ item }">
          <span class="cp-name">{{ item.designation }}</span>
        </template>
        <template v-slot:[`item.description`]="{ item }">
          <span class="cp-desc">{{ item.description || '—' }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="ov-row-actions cp-actions">
            <button type="button" class="ov-icon-btn" title="Modifier" @click="editPays(item)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
            <button type="button" class="ov-icon-btn ov-icon-btn--danger" title="Supprimer" @click="confirmDelete(item)">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- ===== PANNEAU AJOUT / MODIFICATION ===== -->
    <v-navigation-drawer v-model="drawerOpen" right temporary fixed width="420" class="ov-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ isEditing ? 'Modification' : 'Nouveau pays' }}</div>
          <div class="ov-card__title">{{ isEditing ? pays.designation || 'Modifier le pays' : $t('country.addCountry') }}</div>
        </div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="drawerOpen = false">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <form class="ov-panel__body" novalidate @submit.prevent="submitForm">
        <div class="ov-field">
          <label class="ov-label" for="cp-designation">{{ $t('country.countryLabel') }}<span class="ov-required">*</span></label>
          <input
            id="cp-designation"
            v-model.trim="$v.pays.designation.$model"
            type="text"
            class="ov-input"
            :class="{ 'is-invalid': designationPaysErrors.length }"
            :placeholder="$t('country.countryPlaceholder')"
          />
          <div v-if="designationPaysErrors.length" class="ov-error">{{ designationPaysErrors[0] }}</div>
        </div>

        <div class="ov-field">
          <label class="ov-label" for="cp-description">{{ $t('country.descriptionLabel') }}</label>
          <textarea
            id="cp-description"
            v-model.trim="pays.description"
            class="ov-textarea"
            rows="3"
            :placeholder="$t('country.descriptionPlaceholder')"
          ></textarea>
        </div>

        <button type="submit" class="cp-hidden-submit" tabindex="-1" aria-hidden="true"></button>
      </form>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="isEditing ? (drawerOpen = false) : reinitForm()">
            {{ isEditing ? 'Annuler' : $t('country.reset') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" :disabled="overlay" @click="submitForm">
            <v-progress-circular v-if="overlay" indeterminate size="16" width="2"></v-progress-circular>
            <v-icon v-else small>mdi-content-save-outline</v-icon>
            {{ $t('country.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- ===== CONFIRMATION DE SUPPRESSION ===== -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <div class="ov-dialog">
        <div class="ov-dialog__head">
          <div class="ov-dialog__title">Supprimer ce pays ?</div>
        </div>
        <div class="ov-dialog__body">
          <div class="ov-note ov-note--warning">
            <v-icon small color="#92400e">mdi-alert-outline</v-icon>
            <span>Le pays <strong>{{ paysToDelete && paysToDelete.designation }}</strong> sera supprimé définitivement.</span>
          </div>
        </div>
        <div class="ov-dialog__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="deleteDialog = false; paysToDelete = null">Annuler</button>
          <button type="button" class="ov-btn ov-btn--danger" :disabled="overlay" @click="supprimerPays">
            <v-icon small>mdi-trash-can-outline</v-icon>
            Supprimer
          </button>
        </div>
      </div>
    </v-dialog>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-warning" type="warning" width="350px" dismissible>{{ warningMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import axios from "axios";
import { API_CREER_PAYS, API_OBTENIR_LISTE_DES_PAYS_DISPONIBLE, HEADERS } from "../globalConfig/globalConstConfig";
import { showErrorMessage, showSuccessMessage, showWarningMessage } from "@/modules/messages/messageProcess";
export default {
  name: "CreerPays",
  data() {
    return {
      overlay: false,
      errorMsg: null,
      successMsg: null,
      warningMsg: null,
      drawerOpen: false,
      search: "",
      loadingList: false,
      paysList: [],

      objectContainList: {
        datas: [],
      },

      pays: {
        id: null,
        designation: null,
        description: null,
      },

      tableHeaders: [
        { text: this.$t('country.countryLabel'), value: 'designation', sortable: true },
        { text: this.$t('country.descriptionLabel'), value: 'description', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '100px' },
      ],

      deleteDialog: false,
      paysToDelete: null,
    };
  },

  validations: {
    pays: {
      designation: {
        required,
      },
    },
  },

  methods: {
    reinitForm() {
      this.$v.$reset();
      this.pays = { id: null, designation: null, description: null };
      this.objectContainList.datas = [];
    },

    // RECUPERER LA LISTE DES PAYS
    async obtenirListePays() {
      this.loadingList = true;
      await axios
        .post(API_OBTENIR_LISTE_DES_PAYS_DISPONIBLE, {}, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200 && response.data.items) {
            this.paysList = response.data.items;
          }
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || "Impossible de charger les pays.";
          showErrorMessage();
        })
        .finally(() => {
          this.loadingList = false;
        });
    },

    openCreate() {
      this.reinitForm();
      this.drawerOpen = true;
    },

    // CREATION (POST) OU MODIFICATION (PUT, AVEC L'ID) D'UN PAYS
    async creerPays() {
      this.overlay = true;
      const pays = Object.assign({}, this.pays);
      if (!pays.id) delete pays.id;
      this.objectContainList.datas = [pays];
      const request = this.isEditing ? axios.put : axios.post;
      await request(API_CREER_PAYS, this.objectContainList, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.drawerOpen = false;
              this.obtenirListePays();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else if (response.status == 204) {
            this.warningMsg = this.$t('country.createError');
            showWarningMessage();
          } else {
            this.errorMsg = this.$t('country.createImpossible');
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || this.$t('country.createImpossible');
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
          this.objectContainList.datas = [];
        });
    },

    // EDITION D'UN PAYS
    editPays(item) {
      this.$v.$reset();
      this.pays = { id: item.id, designation: item.designation, description: item.description };
      this.drawerOpen = true;
    },

    // CONFIRMER SUPPRESSION
    confirmDelete(item) {
      this.paysToDelete = item;
      this.deleteDialog = true;
    },

    // SUPPRESSION D'UN PAYS
    async supprimerPays() {
      if (!this.paysToDelete) return;
      this.overlay = true;
      await axios
        .delete(`${API_CREER_PAYS}/${this.paysToDelete.id}`, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          const status = response.data && response.data.status;
          if (response.status == 200 && (!status || status.code == 800)) {
            this.successMsg = (status && status.message) || "Pays supprimé.";
            showSuccessMessage();
            this.obtenirListePays();
          } else {
            this.errorMsg = (status && status.message) || "Suppression impossible.";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || "Suppression impossible.";
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
          this.deleteDialog = false;
          this.paysToDelete = null;
        });
    },

    // SOUMISSION DU FORMULAIRE
    submitForm() {
      if (this.overlay) return;
      this.$v.$touch();
      if (this.$v.pays.$invalid) {
        this.errorMsg = this.$t('country.missingInfo');
        showErrorMessage();
      } else {
        this.creerPays();
      }
    },
  },

  computed: {
    isEditing() {
      return !!this.pays.id;
    },
    designationPaysErrors() {
      const errors = [];
      if (!this.$v.pays.designation.$dirty) return errors;
      !this.$v.pays.designation.required &&
        errors.push(this.$t('country.designationRequired'));
      return errors;
    },
  },

  watch: {
    // Fermer le panneau (clic à l'extérieur compris) réinitialise le formulaire
    drawerOpen(open) {
      if (!open) this.reinitForm();
    },
  },

  mounted() {
    this.obtenirListePays();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.cp-name {
  font-weight: 700;
  color: $ov-navy;
}

.cp-desc {
  color: $ov-muted;
}

.cp-actions {
  justify-content: flex-end;
}

.cp-hidden-submit {
  display: none;
}
</style>
